<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { UiText, UiIcon, UiButton, UiInfoBox, UiTooltip } from "@shopexperts/shared";
import type {
  AdminExpertOrganization,
  AdminWalletTransaction,
  AdminWalletTransactionsQueryParams,
} from "@shopexperts/legacy-api";
import {
  useAdminOrganizationWalletTransactions,
  useReconcileAdminWalletAllocations,
} from "@/app/composables/wallet";
import AdminWalletTransactionFilters from "./AdminWalletTransactionFilters.vue";
import AdminWalletTransactionCard from "./AdminWalletTransactionCard.vue";
import CreateWalletTransactionForm from "./CreateWalletTransactionForm.vue";
import ReleaseHoldDialog from "./ReleaseHoldDialog.vue";
import DeleteWalletTransactionDialog from "./DeleteWalletTransactionDialog.vue";
import {
  canDeleteAdminWalletTransaction,
} from "./useAdminWalletTransaction";

const props = defineProps<{
  organization?: AdminExpertOrganization | null;
  organizationId: number | null | undefined;
}>();

const organizationId = computed(() => props.organizationId);
const hasOrg = computed(() => typeof organizationId.value === "number" && organizationId.value > 0);
const organizationLabel = computed(() => {
  if (props.organization?.name) return props.organization.name;
  return hasOrg.value ? `Organization #${organizationId.value}` : null;
});

const filters = ref<AdminWalletTransactionsQueryParams>({});

const { transactions, isLoading, error, hasNextPage, fetchNextPage, isFetchingNextPage } =
  useAdminOrganizationWalletTransactions({
    organizationId,
    filters,
    enabled: hasOrg,
  });

const hasActiveFilters = computed(() => Object.keys(filters.value).length > 0);
const nowMs = ref(Date.now());
let deleteWindowTimer: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
  deleteWindowTimer = setInterval(() => {
    nowMs.value = Date.now();
  }, 30_000);
});

onUnmounted(() => {
  if (deleteWindowTimer) clearInterval(deleteWindowTimer);
});

const latestTransactionIdByWallet = computed(() => {
  const latestByWallet = new Map<number, number>();
  for (const transaction of transactions.value) {
    const current = latestByWallet.get(transaction.wallet_id);
    if (current === undefined || transaction.id > current) {
      latestByWallet.set(transaction.wallet_id, transaction.id);
    }
  }
  return latestByWallet;
});

function canDeleteTransaction(transaction: AdminWalletTransaction) {
  return canDeleteAdminWalletTransaction(transaction, {
    hasActiveFilters: hasActiveFilters.value,
    latestLoadedTransactionId: latestTransactionIdByWallet.value.get(transaction.wallet_id),
    nowMs: nowMs.value,
  });
}

const { mutate: reconcileAllocations, isPending: isReconciling } =
  useReconcileAdminWalletAllocations();

function reconcileWallet() {
  if (!isPositiveOrgId(organizationId.value)) return;
  reconcileAllocations({ organizationId: organizationId.value });
}

function isPositiveOrgId(value: number | null | undefined): value is number {
  return typeof value === "number" && value > 0;
}

const createOpen = ref(false);
const releaseTarget = ref<AdminWalletTransaction | null>(null);
const releaseOpen = ref(false);
const deleteTarget = ref<AdminWalletTransaction | null>(null);
const deleteOpen = ref(false);

function openCreate() {
  createOpen.value = true;
}

defineExpose({ openCreate });

function onReleaseHold(transaction: AdminWalletTransaction) {
  releaseTarget.value = transaction;
  releaseOpen.value = true;
}

function onDeleteTransaction(transaction: AdminWalletTransaction) {
  deleteTarget.value = transaction;
  deleteOpen.value = true;
}

// Infinite scroll observer
const sentinel = ref<HTMLElement | null>(null);
let observer: IntersectionObserver | null = null;

watch(sentinel, (el) => {
  if (observer) observer.disconnect();
  if (!el) return;
  observer = new IntersectionObserver(
    (entries) => {
      if (entries[0]?.isIntersecting && hasNextPage.value && !isFetchingNextPage.value) {
        fetchNextPage();
      }
    },
    { rootMargin: "200px" },
  );
  observer.observe(el);
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <div v-if="!hasOrg">
      <UiInfoBox variant="warning" title="No wallet available">
        <UiText as="span" variant="p-small">
          <span>This expert has no linked organization, so transactions can't be loaded.</span>
        </UiText>
      </UiInfoBox>
    </div>

    <template v-else>
      <div class="flex items-center justify-between gap-3">
        <div class="flex flex-col gap-1">
          <UiText as="h3" variant="h6" class="font-semibold text-se-dark" text="Wallet transactions" />
          <UiText
            v-if="organizationLabel"
            as="p"
            variant="p-small"
            class="text-se-gray-600"
            :text="organizationLabel"
          />
        </div>
        <div class="flex items-center gap-2">
          <UiTooltip
            content="Rebuild the links between debits and the released holds or credits that funded them. Idempotent — existing links are kept and only gaps are filled."
          >
            <UiButton
              variant="outline"
              size="sm"
              label="Reconcile"
              icon="refresh-cw"
              :loading="isReconciling"
              @click="reconcileWallet"
            />
          </UiTooltip>
          <UiButton variant="primary" size="sm" label="New transaction" icon="plus-square" @click="openCreate" />
        </div>
      </div>

      <AdminWalletTransactionFilters v-model="filters" />

      <!-- Error -->
      <UiInfoBox v-if="error" variant="error" title="Couldn't load transactions">
        <UiText as="span" variant="p-small">
          <span>Something went wrong loading the transactions. Refresh to try again.</span>
        </UiText>
      </UiInfoBox>

      <!-- Loading skeleton -->
      <div v-else-if="isLoading" class="flex flex-col gap-3">
        <div v-for="n in 4" :key="n" class="h-28 animate-pulse rounded-xl bg-se-gray-100" />
      </div>

      <!-- Empty -->
      <div
        v-else-if="transactions.length === 0"
        class="flex flex-col items-center gap-3 rounded-xl border border-dashed border-se-gray-300 py-16 text-center"
      >
        <div class="flex h-12 w-12 items-center justify-center rounded-full bg-se-gray-100">
          <UiIcon name="wallet" class="h-6 w-6 text-se-gray-500" />
        </div>
        <UiText
          as="h4"
          variant="h6"
          class="font-semibold"
          :text="hasActiveFilters ? 'No transactions match your filters' : 'No transactions yet'"
        />
        <UiText
          as="p"
          variant="p-small"
          class="text-se-gray-600"
          :text="hasActiveFilters
            ? 'Try adjusting or clearing the filters above.'
            : 'Wallet activity will appear here. You can also create a manual transaction.'"
        />
      </div>

      <!-- List -->
      <template v-else>
        <ul class="flex flex-col">
          <AdminWalletTransactionCard
            v-for="(transaction, index) in transactions"
            :key="transaction.id"
            :transaction="transaction"
            :is-last="index === transactions.length - 1"
            :can-delete-transaction="canDeleteTransaction(transaction)"
            @release-hold="onReleaseHold"
            @delete-transaction="onDeleteTransaction"
          />
        </ul>

        <div ref="sentinel" class="h-1" />

        <div v-if="isFetchingNextPage" class="py-3 text-center">
          <UiText as="p" variant="p-small" class="text-se-gray-500" text="Loading more…" />
        </div>
      </template>
    </template>

    <CreateWalletTransactionForm
      v-model="createOpen"
      :organization-id="organizationId"
    />

    <ReleaseHoldDialog
      v-model="releaseOpen"
      :transaction="releaseTarget"
      :organization-id="organizationId"
    />

    <DeleteWalletTransactionDialog
      v-model="deleteOpen"
      :transaction="deleteTarget"
      :organization-id="organizationId"
    />
  </div>
</template>
